import { autoinject, bindable, observable, computedFrom, TaskQueue } from 'aurelia-framework';
import { GlobalContextService } from './global-context-service';
import { ApiService } from '../../utils/api-service';
import { EventAggregator } from 'aurelia-event-aggregator';
import { noop } from 'lodash';

@autoinject
export class GlobalSelect {
	@bindable itemName: string;
	@bindable parentItemName: string;
	@bindable optionsUrl: string;
	@bindable itemUrl: string;
	@bindable useListItem: boolean; // if true, the list item is saved when selected (itemUrl is only used for later fetching) 
	@bindable selectedItemLabelResource: string;
	@bindable selectHereLabelResource: string;
	@bindable parentNotSelectedLabelResource: string;
	@bindable noItemsLabelResource: string;
	@bindable itemNameProperty: string = 'name';
	@bindable itemInactiveProperty: string;
	@bindable itemSelectCallback: (item) => void;

	@observable selectedIndex: number = 0;
	@observable parentId: string;
	@observable parentItem: any;
	@observable selectedItemId: any;

	isOpen: boolean;
	availableItems: any[] = [];
	filteredItems: any[] = [];
	filter: string = "";
	forceRefresh: number = 0;
	inputElement: HTMLInputElement;
	containerElement: HTMLElement;
	selectedItemElement: HTMLElement;
	selectedItemParentElement: HTMLElement;

	constructor(
		private api: ApiService,
		private context: GlobalContextService,
		private eventAggregator: EventAggregator,
		private q: TaskQueue) {

		this.isOpen = false;
	}

	@computedFrom("parentId", "selectedItemId", "forceRefresh")
	get selectedItem(): any {
		return this.context.getItem(this.itemName);
	}
	
	@computedFrom("parentId", "parentItem")
	get parentItemSelected(): boolean {
		return this.parentId && this.parentItem;
	}

	attached() {
		if (this.parentItemName != null && this.parentItemName !== '') {
			this.parentId = this.context.getId(this.parentItemName);
			this.parentItem = this.context.getItem(this.parentItemName);

			this.eventAggregator.subscribe(`idChanged-${this.parentItemName}`, id => {
				this.parentId = id;
				this.context.setItem(this.itemName, null);

				// By passing true as an argument we automatically set customer's newest scope for admin user
				this.refreshAvailableItems(true);
			});

			this.eventAggregator.subscribe(`itemChanged-${this.parentItemName}`, item => {
				this.parentItem = item;
			});
		}

		this.refreshAvailableItems();

		document.addEventListener('click', () => this.isOpen = false, true);
	}

	async refreshAvailableItems(setFirstItemToContext: boolean = false) {
		if (this.parentItemName && !this.parentId) {
			return;
		}

		const url = this.optionsUrl.replace('{parentId}', this.parentId);

		this.availableItems = await this.api.fetchJson(url);
		this.filteredItems = this.availableItems;

		// When changing customer set newest scope automatically
		if (setFirstItemToContext) {
			if (this.itemName == 'scope' && this.availableItems.length > 0) {
				this.changeItem(await this.fetchItem(this.availableItems[0].id));
			}			
		} else {
			const selectedItemId = this.context.getId(this.itemName);
			if (selectedItemId && (!this.selectedItem || this.selectedItem.id != selectedItemId)) {

				this.fetchItem(selectedItemId)
					.then(item => {
						this.context.setItem(this.itemName, item);
						this.forceRefresh++;
					});
			}
			else if (!selectedItemId && this.selectedItem) {
				this.context.setItem(this.itemName, null);
			}
		}
	}

	changeItem(item: any): void {
		this.selectedItemId = item.id;
		this.context.setId(this.itemName, item.id);
		this.context.setItem(this.itemName, item);
		this.filteredItems = this.availableItems;
	}

	openSelector() {
		this.isOpen = true;

		// Reset filters when opening item selector
		this.filter = "";
		this.filteredItems = this.availableItems;
		this.selectedIndex = 0;

		// Timeout hack to give Aurelia time to sort DOM under the hood
		setTimeout(() => {
			this.inputElement.focus();
		}, 1)
	}

	// In some cases we explicitly want to close the selector
	closeSelector() {
		this.isOpen = false;
	}

	onControlClick(e: any) {
		e.stopPropagation();

		// Close selector if clicked in the item list
		if (e.target.className == "title") {
			this.closeSelector();
		} else {
			this.openSelector();
		}
	}

	onControlKeypress(e: KeyboardEvent) {
		e.stopPropagation();

		if (e.key == "ArrowRight" || e.key == " ") {
			this.openSelector();
		}

		return true;
	}

	async setSelectedItem(item: any): Promise<any> {
		const itemToSave = this.useListItem ? item : await this.fetchItem(item.id);
		
		this.changeItem(itemToSave);
		if (typeof this.itemSelectCallback === 'function') {
			this.itemSelectCallback({ itemToSave });
		}

		this.closeSelector();
	}

	setSelectedIndex(index: number) {
		if (index >= 0 && index <= this.filteredItems.length - 1) {
			this.selectedIndex = index;
		} else if (index > this.filteredItems.length - 1) {
			this.selectedIndex = this.filteredItems.length - 1;
		} else {
			this.selectedIndex = 0;
		}
	}

	private async fetchItem(itemId: string): Promise<any> {
		return await this.api.fetchJson(this.itemUrl.replace('{id}', itemId));
	}

	async filterList(e: any) {
		e.stopPropagation();

		switch (e.key) {
			case "Escape":
				this.closeSelector();
				break;
			case "ArrowDown":
				if (this.filteredItems.length > 0) {
					if (this.filteredItems.length > 1) {
						this.selectedIndex++;
					}

					this.selectedItemParentElement.focus();
					this.scrollSelectedItemToView("start");
				}
				break;
			case "Enter":
				if (this.filteredItems.length > 0) {
					await this.setSelectedItem(this.filteredItems[this.selectedIndex]);
				}
				break;
			default:
				this.filter = e.target.value;
				this.filteredItems = this.filter.length > 0 ? this.availableItems.filter(item => item.name.toLowerCase().includes(this.filter.toLowerCase())) : this.availableItems;
				break;
		}
	}

	async navigateList(e) {
		e.stopPropagation();

		switch (e.key) {
			case "Escape":
				this.closeSelector();
				break;
			case "ArrowDown":
				this.navigateDown()
				break;
			case "ArrowUp":
				// Move back to input if we're at the top of the list
				if (this.selectedIndex == 0) {
					this.inputElement.focus();
				}

				this.navigateUp();
				break;
			case "Enter":
				if (this.filteredItems.length > 0) {
					await this.setSelectedItem(this.filteredItems[this.selectedIndex]);
				}
				break;
			// Append filter on demand and focus back to input
			default: 
				if ((e.keyCode >= 65 && e.keyCode <= 90) || (e.keyCode >= 97 && e.keyCode <= 122) || e.keyCode == 192 || e.keyCode == 221 || e.keyCode == 222) {
					this.filter += e.key;
					this.selectedIndex = 0;
					this.inputElement.focus();
				} 
				break;
		}
	}

	navigateDown() {
		this.setSelectedIndex(this.selectedIndex + 1);
		this.scrollSelectedItemToView("end");

	}
	navigateUp() {
		this.setSelectedIndex(this.selectedIndex - 1);
		this.scrollSelectedItemToView("start");		
	}

	scrollSelectedItemToView(targetAlignment: ScrollLogicalPosition) {
		const element = this.selectedItemParentElement.children[this.selectedIndex];
		if (!this.isSelectedItemElementInView(element)) {
			element.scrollIntoView({ block: targetAlignment });
		}
	}

	isSelectedItemElementInView(el: any) {
		const rect = el.getBoundingClientRect();
		const container = el.parentNode.parentNode;

		// Check if bottom of the element is off the page
		if (rect.top < 0) {
			return false;
		}

		// Check if the element is within the document viewport
		if (rect.bottom > document.documentElement.clientHeight) {
			return false;
		}

		// Check if the element is out of view due to a container scrolling
		const containerRect = container.getBoundingClientRect();
		if (rect.top <= containerRect.top) {
			return false;
		}
		if (rect.bottom > containerRect.bottom) {
			return false;
		}

		return true;
	}
}
